import { useState, useEffect } from 'react';
import {
  Plus,
  Settings,
  FileText,
  Calendar,
  Hash,
  List,
  CheckSquare,
  Type,
  Box,
  Edit2,
  Trash2,
  Copy,
  Play,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Wand2,
  Sparkles,
  X,
  HelpCircle,
  Save,
  Link2,
  ExternalLink,
  Globe,
  ArrowRight,
  ArrowLeftRight,
  GripVertical,
  Bot,
  Mail,
  Workflow,
} from 'lucide-react';
import { Button, Card, Spinner, Badge, useToast } from '@centraoneplus/ui';
import { apiClient } from '@services/api-client';
import { useTenantContextStore } from '@stores/tenantContextStore';
import { channelService, type AISettings } from '@services/channel.service';
import { N8nSettings } from '@components/settings/N8nSettings';
import { ThemedSelect } from '@components/common/ThemedSelect';

/** Extract a user-friendly message from an API error without breaking on unknown shapes. */
function readApiError(err: any, fallback: string): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
}

// Types
interface FormFieldConfig {
  type?: 'input' | 'dropdown' | 'link' | 'textarea' | 'checkbox';
  subtype?: 'text' | 'number' | 'phone' | 'email' | 'url' | 'date';
  hint?: string;
  maxLength?: number;
  isDisabled?: boolean;
  isDeletable?: boolean;
  accessLevel?: number;
}

interface SchemaField {
  id: string;
  key: string;
  displayName: string;
  description?: string;
  fieldType: string;
  isArray: boolean;
  isRequired: boolean;
  extractionHint?: string;
  keywords: string[];
  examples: string[];
  enumValues: string[];
  dateMode?: string;
  mapsToTicketField?: string;
  mapsToLineItem: boolean;
  sortOrder: number;
  formFieldConfig?: FormFieldConfig;
}

interface ExtractionSchema {
  id: string;
  name: string;
  description?: string;
  version: number;
  isActive: boolean;
  extractionContext?: string;
  ticketTypeId?: string;
  dateConfig: {
    defaultDays?: number;
    workingDaysOnly?: boolean;
    keywordMappings?: Record<string, number>;
  };
  ticketType?: {
    id: string;
    name: string;
    slug: string;
  };
  fields: SchemaField[];
  createdAt: string;
  updatedAt: string;
}

interface TicketType {
  id: string;
  name: string;
  slug: string;
}

// Field templates for common energy/business fields
const FIELD_TEMPLATES = [
  {
    key: 'mpan',
    displayName: 'MPAN',
    fieldType: 'STRING',
    extractionHint: 'Extract the 13-digit MPAN number. This is ALWAYS exactly 13 digits. Look for "MPAN:" keyword then extract the 13-digit number. Do NOT confuse with EAC or other numbers.',
    keywords: ['MPAN:', 'MPAN', 'Meter Point', 'Supply Number'],
    examples: ['1300053765675', '1234567890123'],
    mapsToLineItem: true,
  },
  {
    key: 'eac',
    displayName: 'EAC (Annual Consumption)',
    fieldType: 'NUMBER',
    extractionHint: 'Extract EAC (Estimated Annual Consumption) in kWh. This is typically 1,000-500,000. Look for "EAC:" keyword then extract the NUMBER. This is NOT the MPAN - EAC is annual energy usage.',
    keywords: ['EAC:', 'EAC', 'Annual Consumption', 'kWh', 'Consumption:', 'Usage:'],
    examples: ['12000', '50000', '75000'],
    mapsToLineItem: true,
  },
  {
    key: 'start_date',
    displayName: 'Start Date',
    fieldType: 'DATE',
    dateMode: 'KEYWORDS',
    extractionHint: 'Extract contract/supply start date. Look for "Start Date:" keyword. If "ASAP", calculate +3 working days. Output as YYYY-MM-DD.',
    keywords: ['Start Date:', 'Start:', 'Contract Start', 'Supply Start', 'Commence:'],
    mapsToLineItem: true,
  },
  {
    key: 'kva',
    displayName: 'KVA (Capacity)',
    fieldType: 'NUMBER',
    extractionHint: 'Extract KVA capacity value (typically 10-500). Look for "KVA:" keyword.',
    keywords: ['KVA:', 'KVA', 'Capacity:', 'kVA', 'Agreed Capacity:'],
    examples: ['50', '100', '150'],
    mapsToLineItem: true,
  },
  {
    key: 'customer_name',
    displayName: 'Customer Name',
    fieldType: 'STRING',
    extractionHint: 'Extract customer or company name. Look for "Customer:" or "Company:" keywords.',
    keywords: ['Customer:', 'Company:', 'Name:', 'Client:', 'Site Name:'],
    mapsToLineItem: true,
  },
  {
    key: 'fuel_type',
    displayName: 'Fuel Type',
    fieldType: 'ENUM',
    extractionHint: 'Identify if green/renewable or brown/standard energy based on context.',
    keywords: ['Fuel:', 'Type:', 'Green', 'Brown', 'Renewable'],
    enumValues: ['green', 'brown'],
    mapsToLineItem: true,
  },
  {
    key: 'end_date',
    displayName: 'End Date',
    fieldType: 'DATE',
    dateMode: 'KEYWORDS',
    extractionHint: 'Extract contract end date. Look for "End Date:" or "CED:" keywords.',
    keywords: ['End Date:', 'Contract End:', 'Expiry:', 'CED:'],
    mapsToLineItem: true,
  },
  {
    key: 'mprn',
    displayName: 'MPRN (Gas)',
    fieldType: 'STRING',
    extractionHint: 'Extract MPRN (Meter Point Reference Number) for gas. Typically 6-10 digits.',
    keywords: ['MPRN:', 'MPRN', 'Gas Meter', 'Meter Point Reference'],
    examples: ['1234567', '12345678'],
    mapsToLineItem: true,
  },
  {
    key: 'aq',
    displayName: 'AQ (Annual Quantity)',
    fieldType: 'NUMBER',
    extractionHint: 'Extract AQ (Annual Quantity) for gas in kWh. Look for "AQ:" keyword.',
    keywords: ['AQ:', 'AQ', 'Annual Quantity', 'Annual Gas'],
    examples: ['50000', '100000'],
    mapsToLineItem: true,
  },
  {
    key: 'ticketTitle',
    displayName: 'Ticket Title',
    fieldType: 'STRING',
    extractionHint: 'Generate a concise title summarizing the request (e.g., "Quote Request - 3 MPANs").',
    mapsToTicketField: 'title',
    isRequired: true,
  },
  {
    key: 'ticketDescription',
    displayName: 'Ticket Description',
    fieldType: 'STRING',
    extractionHint: 'Summarize the email request in 1-3 sentences.',
    mapsToTicketField: 'description',
  },
  {
    key: 'ticketDueDate',
    displayName: 'Due Date',
    fieldType: 'DATE',
    dateMode: 'KEYWORDS',
    extractionHint: 'Extract due date. If "ASAP" or "urgent", calculate +3 or +2 working days.',
    keywords: ['Due:', 'Due Date:', 'Deadline:', 'Required by:', 'Prices on:', 'Due on:'],
    mapsToTicketField: 'dueDate',
  },
  {
    key: 'quoteGenerateOn',
    displayName: 'Quote Generate Date',
    fieldType: 'DATE',
    dateMode: 'KEYWORDS',
    extractionHint: 'Date when quote should be generated. Look for "Generate quote on", "Offer on" keywords.',
    keywords: ['Generate quote on:', 'Quote on:', 'Offer on:', 'Due on:'],
  },
  {
    key: 'categoryName',
    displayName: 'Category',
    fieldType: 'STRING',
    extractionHint: 'Categorize as Quote, Contract, Renewal, or General based on email content.',
  },
  {
    key: 'labels',
    displayName: 'Labels',
    fieldType: 'ARRAY',
    extractionHint: 'Extract keywords found: Bespoke, Competitive, Due on, Prices on, Generated on.',
    keywords: ['Bespoke', 'Competitive', 'Due on', 'Prices on', 'Generated on'],
  },
  {
    key: 'userName',
    displayName: 'Sender Name',
    fieldType: 'STRING',
    extractionHint: 'Extract sender name from email signature or greeting.',
  },
  {
    key: 'emailSummary',
    displayName: 'Email Summary',
    fieldType: 'STRING',
    extractionHint: 'Summarize the email in 1-3 sentences capturing the main request.',
  },
];

const FIELD_TYPE_ICONS: Record<string, React.ElementType> = {
  STRING: Type,
  NUMBER: Hash,
  DATE: Calendar,
  DATETIME: Calendar,
  BOOLEAN: CheckSquare,
  ENUM: List,
  ARRAY: List,
  OBJECT: Box,
};

const FIELD_TYPES = [
  { value: 'STRING', label: 'Text', description: 'Plain text like names, descriptions' },
  { value: 'NUMBER', label: 'Number', description: 'Numeric values like EAC, KVA' },
  { value: 'DATE', label: 'Date', description: 'Dates with keyword support (ASAP, etc.)' },
  { value: 'DATETIME', label: 'Date & Time', description: 'Date with time' },
  { value: 'BOOLEAN', label: 'Yes/No', description: 'True/false values' },
  { value: 'ENUM', label: 'Select', description: 'Fixed list of options' },
  { value: 'ARRAY', label: 'List', description: 'Multiple values' },
];

const DATE_MODES = [
  { value: 'EXACT', label: 'Exact dates only' },
  { value: 'RELATIVE', label: 'Relative (e.g., "next Monday")' },
  { value: 'KEYWORDS', label: 'Keywords (e.g., "ASAP" → +3 days)' },
  { value: 'AUTO', label: 'Auto-detect' },
];

const TICKET_FIELD_MAPPINGS = [
  { value: '', label: 'No mapping' },
  { value: 'title', label: 'Ticket Title' },
  { value: 'description', label: 'Ticket Description' },
  { value: 'dueDate', label: 'Ticket Due Date' },
];

// Form field UI types for line items
const FORM_FIELD_TYPES = [
  { value: 'input', label: 'Input Field' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'link', label: 'Link/URL' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'checkbox', label: 'Checkbox' },
];

const FORM_FIELD_SUBTYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'phone', label: 'Phone/MPAN' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'URL' },
  { value: 'date', label: 'Date' },
];

// External API Configuration Types and Constants
enum TransformationType {
  NONE = 'none',
  UPPERCASE = 'uppercase',
  LOWERCASE = 'lowercase',
  TRIM = 'trim',
  REPLACE = 'replace',
}

enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
}

enum ContentType {
  JSON = 'application/json',
  FORM_URLENCODED = 'application/x-www-form-urlencoded',
  MULTIPART = 'multipart/form-data',
}

interface FieldTransformation {
  type: TransformationType;
  replacePattern?: string;
  replaceWith?: string;
}

interface RequestFieldMapping {
  id: string;
  sourceField: string;
  targetField: string;
  isRequired: boolean;
  defaultValue?: string;
  transformation?: FieldTransformation;
}

interface ResponseFieldMapping {
  id: string;
  sourceField: string;
  targetField: string;
  transformation?: FieldTransformation;
}

interface ExternalApiConfig {
  enabled: boolean;
  apiUrl: string;
  httpMethod: HttpMethod;
  contentType: ContentType;
  requestMappings: RequestFieldMapping[];
  responseMappings: ResponseFieldMapping[];
}

const HTTP_METHODS = [
  { value: HttpMethod.GET, label: 'GET', description: 'Retrieve data' },
  { value: HttpMethod.POST, label: 'POST', description: 'Send data (most common)' },
  { value: HttpMethod.PUT, label: 'PUT', description: 'Update data' },
];

const CONTENT_TYPES = [
  { value: ContentType.MULTIPART, label: 'Multipart Form Data', description: 'Best for forms with files' },
  { value: ContentType.JSON, label: 'JSON', description: 'Standard API format' },
  { value: ContentType.FORM_URLENCODED, label: 'URL Encoded', description: 'Simple form data' },
];

const TRANSFORMATION_TYPES = [
  { value: TransformationType.NONE, label: 'None', description: 'No transformation' },
  { value: TransformationType.UPPERCASE, label: 'Uppercase', description: 'Convert to UPPERCASE' },
  { value: TransformationType.LOWERCASE, label: 'Lowercase', description: 'Convert to lowercase' },
  { value: TransformationType.TRIM, label: 'Trim', description: 'Remove whitespace' },
  { value: TransformationType.REPLACE, label: 'Replace', description: 'Regex replace' },
];

// Common source fields from line items
const COMMON_SOURCE_FIELDS = [
  { value: 'mpan', label: 'MPAN' },
  { value: 'mprn', label: 'MPRN' },
  { value: 'eac', label: 'EAC' },
  { value: 'aq', label: 'AQ' },
  { value: 'kva', label: 'KVA' },
  { value: 'start_date', label: 'Start Date' },
  { value: 'end_date', label: 'End Date' },
  { value: 'fuel_type', label: 'Fuel Type' },
  { value: 'customer_name', label: 'Customer Name' },
  { value: 'brokerEmail', label: 'Broker Email' },
];

const DEFAULT_EXTERNAL_API_CONFIG: ExternalApiConfig = {
  enabled: false,
  apiUrl: '',
  httpMethod: HttpMethod.POST,
  contentType: ContentType.MULTIPART,
  requestMappings: [],
  responseMappings: [],
};

// Field definition type for selection
interface FieldDefinitionOption {
  id: string;
  key: string;
  name: string;
  fieldType: string;
  keywords: string[];
  examples: string[];
  formFieldConfig?: any;
}

export function ExtractionSchemasSettings() {
  const toast = useToast();
  const { currentTenant } = useTenantContextStore();
  const [schemas, setSchemas] = useState<ExtractionSchema[]>([]);
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinitionOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSchema, setSelectedSchema] = useState<ExtractionSchema | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditSchemaModal, setShowEditSchemaModal] = useState(false);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [editingField, setEditingField] = useState<SchemaField | null>(null);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  // External API Configuration state
  const [externalApiConfig, setExternalApiConfig] = useState<ExternalApiConfig>(DEFAULT_EXTERNAL_API_CONFIG);
  const [isSavingApiConfig, setIsSavingApiConfig] = useState(false);
  const [apiConfigLoaded, setApiConfigLoaded] = useState(false);
  const [apiConfigExpanded, setApiConfigExpanded] = useState(false);

  // AI Auto Reply state
  const [aiSettings, setAiSettings] = useState<AISettings | null>(null);
  const [aiSettingsLoading, setAiSettingsLoading] = useState(true);
  const [aiSettingsSaving, setAiSettingsSaving] = useState(false);
  const [hasEmailConfig, setHasEmailConfig] = useState(false);
  // #18958 — editable draft of the exclude-patterns list (one pattern per
  // line); committed to the API on blur so typing isn't saved per keystroke.
  const [excludePatternsDraft, setExcludePatternsDraft] = useState('');

  // Collapsible sections state
  const [autoReplyExpanded, setAutoReplyExpanded] = useState(true);
  const [extractionExpanded, setExtractionExpanded] = useState(true);
  // n8n Workflows section — collapsed by default so N8nSettings mounts lazily on expand
  const [n8nExpanded, setN8nExpanded] = useState(false);

  useEffect(() => {
    if (currentTenant?.id) {
      loadData();
      loadAISettings();
    }
  }, [currentTenant?.id]);

  // Load External API Configuration when schema is selected
  useEffect(() => {
    if (selectedSchema?.ticketTypeId) {
      loadExternalApiConfig(selectedSchema.ticketTypeId);
    } else {
      // Reset API config if no ticket type
      setExternalApiConfig(DEFAULT_EXTERNAL_API_CONFIG);
      setApiConfigLoaded(false);
      setApiConfigExpanded(false);
    }
  }, [selectedSchema?.ticketTypeId]);

  const loadExternalApiConfig = async (ticketTypeId: string) => {
    try {
      const response = await apiClient.get(`/extraction-configs/settings/${ticketTypeId}`);
      if (response.data) {
        // Support both new externalApiConfig and legacy Horizon fields
        if (response.data.externalApiConfig) {
          setExternalApiConfig(response.data.externalApiConfig);
        } else if (response.data.horizonEnabled) {
          // Migrate legacy Horizon config to new format
          setExternalApiConfig({
            enabled: response.data.horizonEnabled,
            apiUrl: response.data.horizonApiUrl || '',
            httpMethod: HttpMethod.POST,
            contentType: ContentType.MULTIPART,
            requestMappings: [
              { id: '1', sourceField: 'mpan', targetField: 'mpan', isRequired: true },
              { id: '2', sourceField: 'eac', targetField: 'eac', isRequired: true },
              { id: '3', sourceField: 'kva', targetField: 'kva', isRequired: false },
              { id: '4', sourceField: 'fuel_type', targetField: 'fuel_type', isRequired: false, defaultValue: response.data.horizonEnergyType || 'electricity' },
              { id: '5', sourceField: 'brokerEmail', targetField: 'brokerEmail', isRequired: false },
              { id: '6', sourceField: 'start_date', targetField: 'StartDate', isRequired: false },
            ],
            responseMappings: [
              { id: '1', sourceField: 'quotationLink', targetField: 'quotationLink' },
              { id: '2', sourceField: 'brokerLink', targetField: 'brokerLink' },
            ],
          });
        } else {
          setExternalApiConfig(DEFAULT_EXTERNAL_API_CONFIG);
        }
      }
      setApiConfigLoaded(true);
    } catch (error: any) {
      // Settings might not exist yet - that's OK
      if (error.response?.status !== 404) {
        console.error('Failed to load external API config:', error);
        toast.error('Failed to load external API config', readApiError(error, 'Please try again.'));
      }
      setApiConfigLoaded(true);
    }
  };

  const saveExternalApiConfig = async () => {
    if (!selectedSchema?.ticketTypeId) return;

    setIsSavingApiConfig(true);
    try {
      await apiClient.put(`/extraction-configs/settings/${selectedSchema.ticketTypeId}`, {
        externalApiConfig: externalApiConfig.enabled ? externalApiConfig : { ...externalApiConfig, enabled: false },
      });
      toast.success('External API config saved');
    } catch (error) {
      console.error('Failed to save external API config:', error);
      toast.error('Failed to save external API config', readApiError(error, 'Please try again.'));
    } finally {
      setIsSavingApiConfig(false);
    }
  };

  // Helper functions for managing mappings
  const addRequestMapping = () => {
    const newMapping: RequestFieldMapping = {
      id: Date.now().toString(),
      sourceField: '',
      targetField: '',
      isRequired: false,
    };
    setExternalApiConfig({
      ...externalApiConfig,
      requestMappings: [...externalApiConfig.requestMappings, newMapping],
    });
  };

  const updateRequestMapping = (id: string, updates: Partial<RequestFieldMapping>) => {
    setExternalApiConfig({
      ...externalApiConfig,
      requestMappings: externalApiConfig.requestMappings.map(m =>
        m.id === id ? { ...m, ...updates } : m
      ),
    });
  };

  const removeRequestMapping = (id: string) => {
    setExternalApiConfig({
      ...externalApiConfig,
      requestMappings: externalApiConfig.requestMappings.filter(m => m.id !== id),
    });
  };

  const addResponseMapping = () => {
    const newMapping: ResponseFieldMapping = {
      id: Date.now().toString(),
      sourceField: '',
      targetField: '',
    };
    setExternalApiConfig({
      ...externalApiConfig,
      responseMappings: [...externalApiConfig.responseMappings, newMapping],
    });
  };

  const updateResponseMapping = (id: string, updates: Partial<ResponseFieldMapping>) => {
    setExternalApiConfig({
      ...externalApiConfig,
      responseMappings: externalApiConfig.responseMappings.map(m =>
        m.id === id ? { ...m, ...updates } : m
      ),
    });
  };

  const removeResponseMapping = (id: string) => {
    setExternalApiConfig({
      ...externalApiConfig,
      responseMappings: externalApiConfig.responseMappings.filter(m => m.id !== id),
    });
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [schemasRes, typesRes, fieldsRes] = await Promise.all([
        apiClient.get('/extraction-schemas'),
        apiClient.get('/ticket-types'),
        apiClient.get('/field-definitions?isActive=true'),
      ]);
      setSchemas(schemasRes.data);
      setTicketTypes(typesRes.data);
      setFieldDefinitions(fieldsRes.data);
    } catch (error) {
      console.error('Failed to load extraction schemas:', error);
      toast.error('Failed to load extraction schemas', readApiError(error, 'Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadAISettings = async () => {
    try {
      setAiSettingsLoading(true);
      const response = await channelService.getAISettings();
      setHasEmailConfig(response.hasEmailConfig);
      setAiSettings(response.settings);
      setExcludePatternsDraft((response.settings?.excludePatterns ?? []).join('\n'));
    } catch (err: any) {
      console.error('Failed to load AI settings:', err);
      setHasEmailConfig(false);
      setAiSettings(null);
    } finally {
      setAiSettingsLoading(false);
    }
  };

  // #18958 — AI email processing and auto-reply are two independent settings.
  // The old single toggle sent aiEnabled and autoReplyEnabled together, so
  // turning auto-reply off also killed all AI email processing (drafting,
  // extraction). They are now controlled separately.
  const handleToggleAiEnabled = async (enabled: boolean) => {
    if (!hasEmailConfig) {
      toast.error('No email configuration', 'Please configure an email channel first in Settings > Channels');
      return;
    }

    try {
      setAiSettingsSaving(true);
      // Disabling AI email processing must also stop auto-reply (it can't run
      // without AI); enabling AI leaves auto-reply as the user last set it.
      const updated = await channelService.updateAISettings(
        enabled ? { aiEnabled: true } : { aiEnabled: false, autoReplyEnabled: false },
      );
      setAiSettings(updated);
      toast.success(
        enabled ? 'AI email processing enabled' : 'AI email processing disabled',
        enabled
          ? 'AI will read incoming emails to draft replies and extract fields.'
          : 'AI will no longer process incoming emails.',
      );
    } catch (err: any) {
      toast.error('Failed to update', err.response?.data?.message || 'Could not update AI settings');
    } finally {
      setAiSettingsSaving(false);
    }
  };

  const handleToggleAutoReply = async (enabled: boolean) => {
    if (!hasEmailConfig) {
      toast.error('No email configuration', 'Please configure an email channel first in Settings > Channels');
      return;
    }

    try {
      setAiSettingsSaving(true);
      // Enabling auto-reply implies AI must be on; disabling it leaves AI
      // processing (and draft creation) running.
      const updated = await channelService.updateAISettings(
        enabled ? { autoReplyEnabled: true, aiEnabled: true } : { autoReplyEnabled: false },
      );
      setAiSettings(updated);
      toast.success(
        enabled ? 'Auto-reply enabled' : 'Auto-reply disabled',
        enabled
          ? 'AI will now automatically respond to customer emails based on your knowledge base.'
          : 'AI auto-reply has been turned off. AI will draft replies for agent review instead.',
      );
    } catch (err: any) {
      toast.error('Failed to update', err.response?.data?.message || 'Could not update auto-reply settings');
    } finally {
      setAiSettingsSaving(false);
    }
  };

  // #18958 — generic single-field AI-settings update, used by the draft-mode,
  // exclude-patterns and threshold controls that the UI previously lacked.
  const handleUpdateAiSetting = async (
    patch: Partial<AISettings>,
    successTitle: string,
    successMsg: string,
  ) => {
    try {
      setAiSettingsSaving(true);
      const updated = await channelService.updateAISettings(patch);
      setAiSettings(updated);
      toast.success(successTitle, successMsg);
    } catch (err: any) {
      toast.error('Failed to update', readApiError(err, 'Could not save AI settings'));
    } finally {
      setAiSettingsSaving(false);
    }
  };

  const handleCreateSchema = async (data: Partial<ExtractionSchema>) => {
    try {
      const response = await apiClient.post('/extraction-schemas', data);
      setSchemas([...schemas, response.data]);
      setShowCreateModal(false);
      setSelectedSchema(response.data);
      toast.success('Schema created', `"${response.data.name}" has been created.`);
    } catch (error) {
      console.error('Failed to create schema:', error);
      toast.error('Create failed', readApiError(error, 'Could not create the extraction schema.'));
    }
  };

  const handleUpdateSchema = async (data: Partial<ExtractionSchema>) => {
    if (!selectedSchema) return;
    try {
      const response = await apiClient.put(`/extraction-schemas/${selectedSchema.id}`, data);
      const updatedSchema = { ...selectedSchema, ...response.data };
      setSchemas(schemas.map(s => s.id === selectedSchema.id ? updatedSchema : s));
      setSelectedSchema(updatedSchema);
      setShowEditSchemaModal(false);
      toast.success('Schema updated', `"${updatedSchema.name}" has been saved.`);
    } catch (error) {
      console.error('Failed to update schema:', error);
      toast.error('Save failed', readApiError(error, 'Could not save the extraction schema.'));
    }
  };

  const handleDeleteSchema = async (schemaId: string) => {
    if (!confirm('Are you sure you want to delete this extraction schema?')) return;

    try {
      await apiClient.delete(`/extraction-schemas/${schemaId}`);
      setSchemas(schemas.filter(s => s.id !== schemaId));
      if (selectedSchema?.id === schemaId) {
        setSelectedSchema(null);
      }
      toast.success('Schema deleted', 'The extraction schema has been removed.');
    } catch (error) {
      console.error('Failed to delete schema:', error);
      toast.error('Delete failed', readApiError(error, 'Could not delete the extraction schema.'));
    }
  };

  const handleAddField = () => {
    // Show field selector to pick from existing field definitions
    setShowFieldSelector(true);
  };

  const handleAddCustomField = () => {
    // Add a custom field (manual entry)
    setEditingField(null);
    setShowFieldSelector(false);
    setShowFieldEditor(true);
  };

  const handleAddFromDefinition = async (fieldDef: FieldDefinitionOption) => {
    if (!selectedSchema) return;

    // Map field type from field definition to schema field type
    const fieldTypeMap: Record<string, string> = {
      text: 'STRING',
      textarea: 'STRING',
      number: 'NUMBER',
      decimal: 'NUMBER',
      email: 'STRING',
      phone: 'STRING',
      url: 'STRING',
      date: 'DATE',
      datetime: 'DATETIME',
      select: 'ENUM',
      multiselect: 'ARRAY',
      checkbox: 'BOOLEAN',
      radio: 'ENUM',
    };

    const fieldData = {
      fieldDefinitionId: fieldDef.id,
      key: fieldDef.key,
      displayName: fieldDef.name,
      fieldType: fieldTypeMap[fieldDef.fieldType] || 'STRING',
      keywords: fieldDef.keywords || [],
      examples: fieldDef.examples || [],
      formFieldConfig: fieldDef.formFieldConfig,
      mapsToLineItem: true, // Default to line item field
    };

    try {
      const response = await apiClient.post(
        `/extraction-schemas/${selectedSchema.id}/fields`,
        fieldData
      );
      const updatedSchema = {
        ...selectedSchema,
        fields: [...selectedSchema.fields, response.data],
      };
      setSelectedSchema(updatedSchema);
      setSchemas(schemas.map(s => s.id === selectedSchema.id ? updatedSchema : s));
      setShowFieldSelector(false);
      toast.success('Field added', `"${fieldDef.name}" added to the schema.`);
    } catch (error) {
      console.error('Failed to add field from definition:', error);
      toast.error('Add field failed', readApiError(error, 'Could not add the field to the schema.'));
    }
  };

  const handleEditField = (field: SchemaField) => {
    setEditingField(field);
    setShowFieldEditor(true);
  };

  const handleSaveField = async (fieldData: Partial<SchemaField>) => {
    if (!selectedSchema) return;

    const wasEditing = !!editingField;
    try {
      if (editingField) {
        const response = await apiClient.put(
          `/extraction-schemas/${selectedSchema.id}/fields/${editingField.id}`,
          fieldData
        );
        const updatedFields = selectedSchema.fields.map(f =>
          f.id === editingField.id ? response.data : f
        );
        const updatedSchema = { ...selectedSchema, fields: updatedFields };
        setSelectedSchema(updatedSchema);
        setSchemas(schemas.map(s => s.id === selectedSchema.id ? updatedSchema : s));
      } else {
        const response = await apiClient.post(
          `/extraction-schemas/${selectedSchema.id}/fields`,
          fieldData
        );
        const updatedSchema = {
          ...selectedSchema,
          fields: [...selectedSchema.fields, response.data],
        };
        setSelectedSchema(updatedSchema);
        setSchemas(schemas.map(s => s.id === selectedSchema.id ? updatedSchema : s));
      }
      setShowFieldEditor(false);
      setEditingField(null);
      toast.success(
        wasEditing ? 'Field updated' : 'Field added',
        wasEditing ? 'Your changes have been saved.' : 'The field has been added to the schema.',
      );
    } catch (error) {
      console.error('Failed to save field:', error);
      toast.error(
        wasEditing ? 'Save failed' : 'Add field failed',
        readApiError(error, 'Could not save the field.'),
      );
    }
  };

  const handleDeleteField = async (fieldId: string) => {
    if (!selectedSchema) return;
    if (!confirm('Delete this field?')) return;

    try {
      await apiClient.delete(`/extraction-schemas/${selectedSchema.id}/fields/${fieldId}`);
      const updatedFields = selectedSchema.fields.filter(f => f.id !== fieldId);
      const updatedSchema = { ...selectedSchema, fields: updatedFields };
      setSelectedSchema(updatedSchema);
      setSchemas(schemas.map(s => s.id === selectedSchema.id ? updatedSchema : s));
      toast.success('Field deleted', 'The field has been removed from the schema.');
    } catch (error) {
      console.error('Failed to delete field:', error);
      toast.error('Delete failed', readApiError(error, 'Could not delete the field.'));
    }
  };

  const handleTestExtraction = async () => {
    if (!selectedSchema || !testText.trim()) return;

    setIsTesting(true);
    try {
      // Use BFF endpoint to proxy to AI engine (avoids CORS and provides better error handling)
      // Use longer timeout (2 min) as LLM extraction can take time
      const response = await apiClient.post('/extraction-schemas/test-extraction', {
        schemaDefinition: {
          name: selectedSchema.name,
          extractionContext: selectedSchema.extractionContext,
          ticketFields: selectedSchema.fields
            .filter(f => f.mapsToTicketField)
            .reduce((acc, f) => ({ ...acc, [f.key]: fieldToSchemaField(f) }), {}),
          additionalFields: selectedSchema.fields
            .filter(f => !f.mapsToTicketField && !f.mapsToLineItem)
            .reduce((acc, f) => ({ ...acc, [f.key]: fieldToSchemaField(f) }), {}),
          tasks: selectedSchema.fields.some(f => f.mapsToLineItem)
            ? {
                enabled: true,
                primaryIdentifier: selectedSchema.fields.find(f => f.mapsToLineItem && (f.key === 'mpan' || f.key === 'mprn'))?.key || 'mpan',
                taskDetailsSchema: selectedSchema.fields
                  .filter(f => f.mapsToLineItem)
                  .reduce((acc, f) => ({ ...acc, [f.key]: fieldToSchemaField(f) }), {}),
              }
            : undefined,
          dateConfig: selectedSchema.dateConfig,
        },
        emailSubject: 'Test Email',
        emailBody: testText,
        currentDate: new Date().toISOString().split('T')[0],
      }, { timeout: 120000 });
      setTestResult(response.data);
    } catch (error: any) {
      console.error('Extraction test failed:', error);
      // Show more detailed error message
      const errorMessage = error.response?.data?.message || error.message || 'Test failed';
      setTestResult({ error: errorMessage, details: error.response?.data });
    } finally {
      setIsTesting(false);
    }
  };

  const fieldToSchemaField = (field: SchemaField) => ({
    key: field.key,
    displayName: field.displayName,
    fieldType: field.fieldType,
    description: field.description,
    isArray: field.isArray,
    isRequired: field.isRequired,
    extractionHint: field.extractionHint,
    keywords: field.keywords,
    examples: field.examples,
    enumValues: field.enumValues,
    dateMode: field.dateMode,
    mapsToTicketField: field.mapsToTicketField,
    mapsToLineItem: field.mapsToLineItem,
  });

  const toggleFieldExpanded = (fieldId: string) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(fieldId)) {
      newExpanded.delete(fieldId);
    } else {
      newExpanded.add(fieldId);
    }
    setExpandedFields(newExpanded);
  };

  // Group fields by category
  const ticketFields = selectedSchema?.fields.filter(f => f.mapsToTicketField) || [];
  const lineItemFields = selectedSchema?.fields.filter(f => f.mapsToLineItem) || [];
  const additionalFields = selectedSchema?.fields.filter(f => !f.mapsToTicketField && !f.mapsToLineItem) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary-500" />
          AI Configuration
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Configure AI features including auto-reply and field extraction
        </p>
      </div>

      {/* AI Auto Reply Settings - Collapsible */}
      <Card className="overflow-hidden">
        <button
          onClick={() => setAutoReplyExpanded(!autoReplyExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <Mail className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">AI Auto Reply</h3>
                {aiSettings?.autoReplyEnabled && (
                  <Badge variant="success" className="text-xs">Active</Badge>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Automatically respond to customer emails using your knowledge base
              </p>
            </div>
          </div>
          {autoReplyExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {autoReplyExpanded && (
          <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
            <div className="pt-4">
              {aiSettingsLoading ? (
                <div className="animate-pulse h-16 bg-gray-200 dark:bg-gray-700 rounded" />
              ) : !hasEmailConfig ? (
                <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Email channel required
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                      To enable AI auto-reply, you need to configure an email channel first.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* #18958 — AI email processing: the master switch. Keeps
                      running (drafting, extraction) independently of auto-reply. */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          Enable AI Email Processing
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        When enabled, AI reads incoming emails to draft replies and extract fields from your knowledge base. Required for auto-reply.
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleAiEnabled(!aiSettings?.aiEnabled);
                      }}
                      disabled={aiSettingsSaving}
                      role="switch"
                      aria-checked={!!aiSettings?.aiEnabled}
                      aria-label="Enable AI Email Processing"
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        aiSettings?.aiEnabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                      } ${aiSettingsSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          aiSettings?.aiEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {aiSettings?.aiEnabled && (
                    <div className="space-y-4 pl-4 border-l-2 border-gray-100 dark:border-gray-800">
                      {/* Auto-reply: now independent of AI email processing. */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              Enable AI Auto Reply
                            </p>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            When enabled, AI automatically sends replies to incoming emails. When off, AI still drafts replies for agents to review.
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleAutoReply(!aiSettings?.autoReplyEnabled);
                          }}
                          disabled={aiSettingsSaving}
                          role="switch"
                          aria-checked={!!aiSettings?.autoReplyEnabled}
                          aria-label="Enable AI Auto Reply"
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                            aiSettings?.autoReplyEnabled ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                          } ${aiSettingsSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              aiSettings?.autoReplyEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {aiSettings?.autoReplyEnabled && (
                        <>
                          <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                            <p className="text-sm text-indigo-800 dark:text-indigo-200">
                              <strong>How it works:</strong> When a customer sends an email, AI will search your knowledge base for relevant articles.
                              If a confident answer is found (above {((aiSettings?.autoReplyConfidenceThreshold ?? 0.5) * 100).toFixed(0)}% confidence),
                              an automatic reply will be sent. Otherwise, a draft will be created for agent review.
                            </p>
                          </div>

                          {/* Confidence Threshold Slider — #18958 raised the cap
                              to 99% so draft-only / high-confidence gates can be set. */}
                          <div className="mt-4 space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Confidence Threshold
                              </label>
                              <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                {((aiSettings?.autoReplyConfidenceThreshold ?? 0.5) * 100).toFixed(0)}%
                              </span>
                            </div>
                            <input
                              type="range"
                              min="30"
                              max="99"
                              step="1"
                              value={(aiSettings?.autoReplyConfidenceThreshold ?? 0.5) * 100}
                              onChange={async (e) => {
                                const newThreshold = parseInt(e.target.value) / 100;
                                await handleUpdateAiSetting(
                                  { autoReplyConfidenceThreshold: newThreshold },
                                  'Threshold updated',
                                  `Auto-reply threshold set to ${e.target.value}%`,
                                );
                              }}
                              disabled={aiSettingsSaving}
                              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                            />
                            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                              <span>30% (More auto-replies)</span>
                              <span>99% (Only near-certain replies)</span>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Draft Mode — #18958: previously had no UI control. */}
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Draft Mode
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Controls when AI creates a draft for an agent instead of sending automatically.
                        </p>
                        <select
                          value={aiSettings?.draftMode ?? 'low_confidence'}
                          onChange={async (e) => {
                            await handleUpdateAiSetting(
                              { draftMode: e.target.value as AISettings['draftMode'] },
                              'Draft mode updated',
                              'AI draft behaviour has been saved.',
                            );
                          }}
                          disabled={aiSettingsSaving}
                          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                          <option value="low_confidence">Draft only when confidence is low (auto-send confident replies)</option>
                          <option value="always">Always draft — never auto-send</option>
                          <option value="handoff_only">Draft only when a handoff to an agent is needed</option>
                        </select>
                      </div>

                      {/* Exclude Patterns — #18958: previously had no UI control. */}
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Exclude Patterns
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Emails whose subject contains any of these phrases are skipped by AI. One phrase per line.
                        </p>
                        <textarea
                          value={excludePatternsDraft}
                          onChange={(e) => setExcludePatternsDraft(e.target.value)}
                          onBlur={async () => {
                            const patterns = excludePatternsDraft
                              .split('\n')
                              .map((p) => p.trim())
                              .filter((p) => p.length > 0);
                            const current = aiSettings?.excludePatterns ?? [];
                            // Skip the save if nothing actually changed.
                            if (JSON.stringify(patterns) === JSON.stringify(current)) {
                              setExcludePatternsDraft(patterns.join('\n'));
                              return;
                            }
                            await handleUpdateAiSetting(
                              { excludePatterns: patterns },
                              'Exclude patterns updated',
                              patterns.length
                                ? `${patterns.length} pattern${patterns.length === 1 ? '' : 's'} saved.`
                                : 'All exclude patterns removed.',
                            );
                            setExcludePatternsDraft(patterns.join('\n'));
                          }}
                          disabled={aiSettingsSaving}
                          rows={3}
                          placeholder={'e.g.\nout of office\nnewsletter\nunsubscribe'}
                          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Field Extraction Section - Collapsible */}
      <Card className="overflow-hidden">
        <button
          onClick={() => setExtractionExpanded(!extractionExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Sparkles className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Field Extraction Schemas</h3>
                <Badge variant="default" className="text-xs">{schemas.length} schema{schemas.length !== 1 ? 's' : ''}</Badge>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Configure AI-powered field extraction for incoming emails
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowCreateModal(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1" />
              New Schema
            </Button>
            {extractionExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </button>

        {extractionExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700">
            {/* Info Banner */}
            <div className="p-4 bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800">
              <div className="flex items-start gap-3">
                <Wand2 className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-primary-800 dark:text-primary-300">
                    AI-Powered Extraction
                  </p>
                  <p className="text-sm text-primary-700 dark:text-primary-400 mt-1">
                    Define fields with <strong>keywords</strong> and <strong>extraction hints</strong> to help AI accurately extract data.
                    Each keyword tells AI what label to look for, then extract the value that follows.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4">
              {/* Full-width, height-capped list; a schema's details open in a
                  modal (below) instead of a side panel that left blank space. */}
              <div className="flex flex-col gap-6">
        {/* Schema List */}
        <div className="w-full">
          <Card className="scrollbar-thin max-h-[320px] divide-y divide-gray-200 overflow-y-auto 2xl:max-h-[calc(100vh-260px)] dark:divide-gray-700">
            {schemas.length === 0 ? (
              <div className="p-6 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3 dark:text-gray-500" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No extraction schemas yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => setShowCreateModal(true)}
                >
                  Create First Schema
                </Button>
              </div>
            ) : (
              schemas.map((schema) => (
                <div
                  key={schema.id}
                  onClick={() => setSelectedSchema(schema)}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedSchema?.id === schema.id
                      ? 'bg-primary-50 dark:bg-primary-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {schema.name}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {schema.ticketType?.name || 'All ticket types'}
                      </p>
                    </div>
                    <Badge variant={schema.isActive ? 'success' : 'default'} className="flex-shrink-0">
                      {schema.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {schema.fields.length} field{schema.fields.length !== 1 ? 's' : ''} • v{schema.version}
                  </p>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>
    </div>
  </div>
)}
      </Card>

      {/* n8n Workflows Section - Collapsible */}
      <Card className="overflow-hidden">
        <button
          onClick={() => setN8nExpanded(!n8nExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Workflow className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">n8n Workflows</h3>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Manage n8n workflow integrations and triggers
              </p>
            </div>
          </div>
          {n8nExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {n8nExpanded && (
          <div className="px-4 pb-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <N8nSettings embedded />
          </div>
        )}
      </Card>

      {/* Schema Details Modal — opened by clicking a schema in the list */}
      {selectedSchema && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label={selectedSchema.name}
            className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800"
          >
            <button
              type="button"
              onClick={() => setSelectedSchema(null)}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
                <Card>
                  {/* Schema Header */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between pr-10">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {selectedSchema.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {selectedSchema.ticketType ? (
                            <span className="inline-flex items-center gap-1">
                              Linked to: <Badge variant="info">{selectedSchema.ticketType.name}</Badge>
                            </span>
                          ) : (
                            <span className="text-amber-600 dark:text-amber-400">
                              Not linked to any ticket type - click edit to link
                            </span>
                          )}
                        </p>
                        {selectedSchema.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {selectedSchema.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowEditSchemaModal(true)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSchema(selectedSchema.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>

                    {selectedSchema.extractionContext && (
                      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                          AI Context Instructions
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {selectedSchema.extractionContext}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Fields by Category */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        Extraction Fields
                      </h4>
                      <Button variant="outline" size="sm" onClick={handleAddField}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Field
                      </Button>
                    </div>

                    {/* Ticket Fields */}
                    {ticketFields.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">
                          Ticket Fields
                        </h5>
                        <FieldsList
                          fields={ticketFields}
                          expandedFields={expandedFields}
                          onToggle={toggleFieldExpanded}
                          onEdit={handleEditField}
                          onDelete={handleDeleteField}
                        />
                      </div>
                    )}

                    {/* Line Item Fields */}
                    {lineItemFields.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">
                          Line Item Fields (per MPAN/MPRN)
                        </h5>
                        <FieldsList
                          fields={lineItemFields}
                          expandedFields={expandedFields}
                          onToggle={toggleFieldExpanded}
                          onEdit={handleEditField}
                          onDelete={handleDeleteField}
                        />
                      </div>
                    )}

                    {/* Additional Fields */}
                    {additionalFields.length > 0 && (
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">
                          Additional Fields
                        </h5>
                        <FieldsList
                          fields={additionalFields}
                          expandedFields={expandedFields}
                          onToggle={toggleFieldExpanded}
                          onEdit={handleEditField}
                          onDelete={handleDeleteField}
                        />
                      </div>
                    )}

                    {selectedSchema.fields.length === 0 && (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No fields configured yet.</p>
                        <p className="text-xs mt-1">Add fields to define what data to extract from emails.</p>
                      </div>
                    )}
                  </div>

                  {/* External API Configuration Section */}
                  {selectedSchema.ticketTypeId && apiConfigLoaded && (
                    <div className="border-b border-gray-200 dark:border-gray-700">
                      {/* Collapsible Header */}
                      <button
                        onClick={() => setApiConfigExpanded(!apiConfigExpanded)}
                        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Globe className="h-5 w-5 text-primary-500" />
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            External API Configuration
                          </h4>
                          {externalApiConfig.enabled && (
                            <Badge variant="success" className="text-xs ml-2">Enabled</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {externalApiConfig.enabled && externalApiConfig.requestMappings.length > 0 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {externalApiConfig.requestMappings.length} request, {externalApiConfig.responseMappings.length} response mappings
                            </span>
                          )}
                          {apiConfigExpanded ? (
                            <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          )}
                        </div>
                      </button>

                      {/* Expanded Content */}
                      {apiConfigExpanded && (
                        <div className="p-4 pt-0 space-y-6">
                          {/* Enable Toggle */}
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={externalApiConfig.enabled}
                              onChange={(e) => setExternalApiConfig({ ...externalApiConfig, enabled: e.target.checked })}
                              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                Enable External API Integration
                              </span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Call external API after line item creation to generate quotes, links, or fetch additional data
                              </p>
                            </div>
                          </label>

                          {externalApiConfig.enabled && (
                            <div className="space-y-6 pl-7">
                              {/* API Endpoint Configuration */}
                              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-4">
                                <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                  <Link2 className="h-4 w-4" />
                                  API Endpoint
                                </h5>

                                <div>
                                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                    API URL *
                                  </label>
                                  <input
                                    type="url"
                                    value={externalApiConfig.apiUrl}
                                    onChange={(e) => setExternalApiConfig({ ...externalApiConfig, apiUrl: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="https://api.example.com/v1/quotes/generate"
                                  />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                      HTTP Method
                                    </label>
                                    <ThemedSelect
                                      value={externalApiConfig.httpMethod}
                                      onChange={(e) => setExternalApiConfig({ ...externalApiConfig, httpMethod: e.target.value as HttpMethod })}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                      {HTTP_METHODS.map((m) => (
                                        <option key={m.value} value={m.value}>{m.label} - {m.description}</option>
                                      ))}
                                    </ThemedSelect>
                                  </div>

                                  <div>
                                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                                      Content Type
                                    </label>
                                    <ThemedSelect
                                      value={externalApiConfig.contentType}
                                      onChange={(e) => setExternalApiConfig({ ...externalApiConfig, contentType: e.target.value as ContentType })}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                      {CONTENT_TYPES.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                      ))}
                                    </ThemedSelect>
                                  </div>
                                </div>
                              </div>

                              {/* Request Field Mappings */}
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    <ArrowRight className="h-4 w-4 text-blue-500" />
                                    Request Mappings
                                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(Line Item → API Request)</span>
                                  </h5>
                                  <Button variant="outline" size="sm" onClick={addRequestMapping}>
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add Field
                                  </Button>
                                </div>

                                {externalApiConfig.requestMappings.length === 0 ? (
                                  <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
                                    <ArrowRight className="h-8 w-8 text-gray-400 mx-auto mb-2 dark:text-gray-500" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No request mappings configured</p>
                                    <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">Add mappings to define which line item fields to send to the API</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {externalApiConfig.requestMappings.map((mapping) => (
                                      <RequestMappingRow
                                        key={mapping.id}
                                        mapping={mapping}
                                        lineItemFields={lineItemFields}
                                        onUpdate={(updates) => updateRequestMapping(mapping.id, updates)}
                                        onRemove={() => removeRequestMapping(mapping.id)}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Response Field Mappings */}
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    <ArrowLeftRight className="h-4 w-4 text-green-500" />
                                    Response Mappings
                                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(API Response → Line Item)</span>
                                  </h5>
                                  <Button variant="outline" size="sm" onClick={addResponseMapping}>
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add Field
                                  </Button>
                                </div>

                                {externalApiConfig.responseMappings.length === 0 ? (
                                  <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700">
                                    <ArrowLeftRight className="h-8 w-8 text-gray-400 mx-auto mb-2 dark:text-gray-500" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No response mappings configured</p>
                                    <p className="text-xs text-gray-400 mt-1 dark:text-gray-500">Add mappings to store API response data in line items</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {externalApiConfig.responseMappings.map((mapping) => (
                                      <ResponseMappingRow
                                        key={mapping.id}
                                        mapping={mapping}
                                        onUpdate={(updates) => updateResponseMapping(mapping.id, updates)}
                                        onRemove={() => removeResponseMapping(mapping.id)}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Save Button */}
                              <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <Button
                                  onClick={saveExternalApiConfig}
                                  disabled={isSavingApiConfig || (externalApiConfig.enabled && !externalApiConfig.apiUrl)}
                                >
                                  {isSavingApiConfig ? (
                                    <Spinner size="sm" className="mr-2" />
                                  ) : (
                                    <Save className="h-4 w-4 mr-2" />
                                  )}
                                  Save API Configuration
                                </Button>
                                {externalApiConfig.apiUrl && (
                                  <a
                                    href={externalApiConfig.apiUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    View Endpoint
                                  </a>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Save when disabled */}
                          {!externalApiConfig.enabled && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={saveExternalApiConfig}
                              disabled={isSavingApiConfig}
                              className="ml-7"
                            >
                              {isSavingApiConfig ? (
                                <Spinner size="sm" className="mr-2" />
                              ) : (
                                <Save className="h-4 w-4 mr-2" />
                              )}
                              Save
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* No ticket type warning */}
                  {!selectedSchema.ticketTypeId && (
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                            Link to a ticket type to enable External API Configuration
                          </p>
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                            Click Edit above to assign this schema to a ticket type
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Test Panel */}
                  <div className="p-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                      Test Extraction
                    </h4>
                    <div className="space-y-3">
                      <textarea
                        value={testText}
                        onChange={(e) => setTestText(e.target.value)}
                        placeholder={`Paste sample email content to test extraction...

    Example:
    Hi Team,

    Please quote for the below MPANs.

    MPAN: 1300053765675
    EAC: 12000
    Start Date: ASAP

    MPAN: 1300088899991
    EAC: 25000

    Thanks`}
                        className="w-full h-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
                      />
                      <Button
                        onClick={handleTestExtraction}
                        disabled={isTesting || !testText.trim()}
                      >
                        {isTesting ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            Run Extraction
                          </>
                        )}
                      </Button>

                      {testResult && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                            Extraction Result
                            {testResult.confidence && (
                              <span className="ml-2 text-primary-600">
                                ({Math.round(testResult.confidence * 100)}% confidence)
                              </span>
                            )}
                          </p>
                          <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-auto max-h-64 font-mono">
                            {JSON.stringify(testResult, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
            </div>
          </div>
        </div>
      )}

      {/* Create Schema Modal */}
      {showCreateModal && (
        <CreateSchemaModal
          ticketTypes={ticketTypes}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateSchema}
        />
      )}

      {/* Field Editor Modal */}
      {showFieldEditor && selectedSchema && (
        <FieldEditorModal
          field={editingField}
          onClose={() => {
            setShowFieldEditor(false);
            setEditingField(null);
          }}
          onSave={handleSaveField}
        />
      )}

      {/* Edit Schema Modal */}
      {showEditSchemaModal && selectedSchema && (
        <EditSchemaModal
          schema={selectedSchema}
          ticketTypes={ticketTypes}
          onClose={() => setShowEditSchemaModal(false)}
          onSave={handleUpdateSchema}
        />
      )}

      {/* Field Selector Modal */}
      {showFieldSelector && selectedSchema && (
        <FieldSelectorModal
          fieldDefinitions={fieldDefinitions}
          existingFieldKeys={selectedSchema.fields.map(f => f.key)}
          onSelectField={handleAddFromDefinition}
          onAddCustom={handleAddCustomField}
          onClose={() => setShowFieldSelector(false)}
        />
      )}
    </div>
  );
}

// Fields List Component
function FieldsList({
  fields,
  expandedFields,
  onToggle,
  onEdit,
  onDelete,
}: {
  fields: SchemaField[];
  expandedFields: Set<string>;
  onToggle: (id: string) => void;
  onEdit: (field: SchemaField) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="space-y-2">
      {fields.map((field) => {
        const Icon = FIELD_TYPE_ICONS[field.fieldType] || Type;
        const isExpanded = expandedFields.has(field.id);

        return (
          <div
            key={field.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
          >
            <div
              onClick={() => onToggle(field.id)}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                )}
                <Icon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {field.displayName}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    ({field.key})
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {field.isRequired && (
                  <Badge variant="error" className="text-xs">Required</Badge>
                )}
                {field.keywords.length > 0 && (
                  <Badge variant="info" className="text-xs">
                    {field.keywords.length} keywords
                  </Badge>
                )}
                <Badge variant="default" className="text-xs">
                  {field.fieldType}
                </Badge>
              </div>
            </div>

            {isExpanded && (
              <div className="p-3 space-y-3 bg-white dark:bg-gray-900">
                {field.extractionHint && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      AI Extraction Hint
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                      {field.extractionHint}
                    </p>
                  </div>
                )}
                {field.keywords.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Keywords (AI looks for these)
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {field.keywords.map((kw, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300 font-mono"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {field.examples.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Example Values
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {field.examples.map((ex, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-600 dark:text-gray-400 font-mono"
                        >
                          {ex}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {field.enumValues.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Allowed Values
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {field.enumValues.map((v, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 rounded text-xs text-primary-700 dark:text-primary-300"
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <Button variant="outline" size="sm" onClick={() => onEdit(field)}>
                    <Edit2 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onDelete(field.id)}>
                    <Trash2 className="h-3 w-3 mr-1 text-red-500" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Create Schema Modal
function CreateSchemaModal({
  ticketTypes,
  onClose,
  onCreate,
}: {
  ticketTypes: TicketType[];
  onClose: () => void;
  onCreate: (data: Partial<ExtractionSchema>) => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    ticketTypeId: '',
    extractionContext: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await onCreate({
        name: formData.name,
        description: formData.description || undefined,
        ticketTypeId: formData.ticketTypeId || undefined,
        extractionContext: formData.extractionContext || undefined,
        isActive: true,
        dateConfig: {
          defaultDays: 3,
          workingDaysOnly: true,
          keywordMappings: {
            asap: 3,
            urgent: 2,
            immediately: 1,
            soon: 5,
          },
        },
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Create Extraction Schema
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Schema Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Energy Quote Extraction"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ticket Type
            </label>
            <ThemedSelect
              value={formData.ticketTypeId}
              onChange={(e) => setFormData({ ...formData, ticketTypeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All ticket types</option>
              {ticketTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </ThemedSelect>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              AI Context Instructions
            </label>
            <textarea
              value={formData.extractionContext}
              onChange={(e) => setFormData({ ...formData, extractionContext: e.target.value })}
              className="w-full h-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Describe what the AI should extract (e.g., 'Extract electricity quote requests with MPAN numbers, EAC values, start dates from energy broker emails.')"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!formData.name.trim() || isCreating}>
              {isCreating ? <Spinner size="sm" className="mr-2" /> : null}
              Create Schema
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Schema Modal
function EditSchemaModal({
  schema,
  ticketTypes,
  onClose,
  onSave,
}: {
  schema: ExtractionSchema;
  ticketTypes: TicketType[];
  onClose: () => void;
  onSave: (data: Partial<ExtractionSchema>) => void;
}) {
  const [formData, setFormData] = useState({
    name: schema.name,
    description: schema.description || '',
    ticketTypeId: schema.ticketTypeId || '',
    extractionContext: schema.extractionContext || '',
    isActive: schema.isActive,
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({
        name: formData.name,
        description: formData.description || undefined,
        ticketTypeId: formData.ticketTypeId || undefined,
        extractionContext: formData.extractionContext || undefined,
        isActive: formData.isActive,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Edit Schema Settings
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Schema Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ticket Type
            </label>
            <ThemedSelect
              value={formData.ticketTypeId}
              onChange={(e) => setFormData({ ...formData, ticketTypeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All ticket types (not linked)</option>
              {ticketTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </ThemedSelect>
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              Link this schema to a ticket type for automatic extraction
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              AI Context Instructions
            </label>
            <textarea
              value={formData.extractionContext}
              onChange={(e) => setFormData({ ...formData, extractionContext: e.target.value })}
              className="w-full h-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Describe what the AI should extract..."
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Schema is active</span>
          </label>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!formData.name.trim() || isSaving}>
              {isSaving ? <Spinner size="sm" className="mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Field Selector Modal - Select from existing field definitions
function FieldSelectorModal({
  fieldDefinitions,
  existingFieldKeys,
  onSelectField,
  onAddCustom,
  onClose,
}: {
  fieldDefinitions: FieldDefinitionOption[];
  existingFieldKeys: string[];
  onSelectField: (field: FieldDefinitionOption) => void;
  onAddCustom: () => void;
  onClose: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter out already added fields and by search query
  const availableFields = fieldDefinitions.filter(f => {
    if (existingFieldKeys.includes(f.key)) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        f.name.toLowerCase().includes(query) ||
        f.key.toLowerCase().includes(query) ||
        f.keywords?.some(k => k.toLowerCase().includes(query))
      );
    }
    return true;
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Add Extraction Field
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Select from existing field definitions or create a custom field
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search fields by name or keyword..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700"
          />
        </div>

        {/* Field List */}
        <div className="flex-1 overflow-y-auto p-4">
          {availableFields.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {availableFields.map((field) => (
                <button
                  key={field.id}
                  onClick={() => onSelectField(field)}
                  className="p-4 text-left border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                >
                  <div className="font-medium text-gray-900 dark:text-white">
                    {field.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {field.key} • {field.fieldType}
                  </div>
                  {field.keywords && field.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {field.keywords.slice(0, 3).map((kw, i) => (
                        <span
                          key={i}
                          className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300"
                        >
                          {kw}
                        </span>
                      ))}
                      {field.keywords.length > 3 && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          +{field.keywords.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No matching fields found' : 'No field definitions available'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Fields are defined in Settings → Field Definitions
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={onAddCustom}>
              <Plus className="h-4 w-4 mr-1" />
              Custom Field
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Field Editor Modal
function FieldEditorModal({
  field,
  onClose,
  onSave,
}: {
  field: SchemaField | null;
  onClose: () => void;
  onSave: (data: Partial<SchemaField>) => void;
}) {
  const [formData, setFormData] = useState<Partial<SchemaField>>({
    key: field?.key || '',
    displayName: field?.displayName || '',
    description: field?.description || '',
    fieldType: field?.fieldType || 'STRING',
    isArray: field?.isArray || false,
    isRequired: field?.isRequired || false,
    extractionHint: field?.extractionHint || '',
    keywords: field?.keywords || [],
    examples: field?.examples || [],
    enumValues: field?.enumValues || [],
    dateMode: field?.dateMode || 'AUTO',
    mapsToTicketField: field?.mapsToTicketField || '',
    mapsToLineItem: field?.mapsToLineItem || false,
    formFieldConfig: field?.formFieldConfig || {
      type: 'input',
      subtype: 'text',
      hint: '',
      maxLength: 0,
      isDisabled: false,
      isDeletable: false,
      accessLevel: 0,
    },
  });
  const [keywordsInput, setKeywordsInput] = useState(field?.keywords?.join(', ') || '');
  const [examplesInput, setExamplesInput] = useState(field?.examples?.join(', ') || '');
  const [enumValuesInput, setEnumValuesInput] = useState(field?.enumValues?.join(', ') || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showTemplates, setShowTemplates] = useState(!field);

  const handleTemplateSelect = (template: typeof FIELD_TEMPLATES[0]) => {
    // Default form field config based on field type
    const getDefaultFormConfig = (): FormFieldConfig => {
      const fieldType = template.fieldType;
      if (fieldType === 'NUMBER') {
        return { type: 'input', subtype: 'number', hint: template.extractionHint || '' };
      } else if (fieldType === 'DATE') {
        return { type: 'input', subtype: 'date', hint: template.extractionHint || '' };
      } else if (fieldType === 'ENUM') {
        return { type: 'dropdown', subtype: 'text', hint: template.extractionHint || '' };
      } else if (template.key === 'mpan' || template.key === 'mprn') {
        return { type: 'input', subtype: 'phone', hint: 'Enter MPAN/MPRN Number', maxLength: 13 };
      }
      return { type: 'input', subtype: 'text', hint: template.extractionHint || '' };
    };

    setFormData({
      ...formData,
      key: template.key,
      displayName: template.displayName,
      fieldType: template.fieldType,
      extractionHint: template.extractionHint || '',
      mapsToTicketField: (template as any).mapsToTicketField || '',
      mapsToLineItem: template.mapsToLineItem || false,
      isRequired: (template as any).isRequired || false,
      dateMode: (template as any).dateMode || 'AUTO',
      formFieldConfig: template.mapsToLineItem ? getDefaultFormConfig() : undefined,
    });
    setKeywordsInput((template as any).keywords?.join(', ') || '');
    setExamplesInput(template.examples?.join(', ') || '');
    setEnumValuesInput((template as any).enumValues?.join(', ') || '');
    setShowTemplates(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({
        ...formData,
        keywords: keywordsInput.split(',').map(k => k.trim()).filter(Boolean),
        examples: examplesInput.split(',').map(e => e.trim()).filter(Boolean),
        enumValues: enumValuesInput.split(',').map(v => v.trim()).filter(Boolean),
        // Only include formFieldConfig if this is a line item field
        formFieldConfig: formData.mapsToLineItem ? formData.formFieldConfig : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl mx-4">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {field ? 'Edit Field' : 'Add Extraction Field'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Configure how AI extracts this field from emails
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Templates */}
        {showTemplates && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Quick Start - Select a template:
            </p>
            <div className="flex flex-wrap gap-2">
              {FIELD_TEMPLATES.map((template) => (
                <button
                  key={template.key}
                  type="button"
                  onClick={() => handleTemplateSelect(template)}
                  className="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                >
                  {template.displayName}
                </button>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Field Key *
              </label>
              <input
                type="text"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
                placeholder="e.g., eac, mpan"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Display Name *
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., EAC (Annual Consumption)"
                required
              />
            </div>
          </div>

          {/* Field Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Field Type *
            </label>
            <ThemedSelect
              value={formData.fieldType}
              onChange={(e) => setFormData({ ...formData, fieldType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {FIELD_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} - {type.description}
                </option>
              ))}
            </ThemedSelect>
          </div>

          {/* Date Mode */}
          {(formData.fieldType === 'DATE' || formData.fieldType === 'DATETIME') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date Interpretation
              </label>
              <ThemedSelect
                value={formData.dateMode}
                onChange={(e) => setFormData({ ...formData, dateMode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {DATE_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
          )}

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
              Keywords (comma-separated)
              <HelpCircle className="h-3 w-3 text-gray-400 dark:text-gray-500" />
            </label>
            <input
              type="text"
              value={keywordsInput}
              onChange={(e) => setKeywordsInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., EAC:, Annual Consumption:, kWh"
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              AI looks for these keywords to find the field, then extracts the value after them
            </p>
          </div>

          {/* Extraction Hint */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              AI Extraction Hint
            </label>
            <textarea
              value={formData.extractionHint}
              onChange={(e) => setFormData({ ...formData, extractionHint: e.target.value })}
              className="w-full h-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Instructions for AI on how to extract this field..."
            />
          </div>

          {/* Examples */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Examples (comma-separated)
            </label>
            <input
              type="text"
              value={examplesInput}
              onChange={(e) => setExamplesInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., 12000, 50000, 75000"
            />
          </div>

          {/* Enum Values */}
          {formData.fieldType === 'ENUM' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Allowed Values (comma-separated) *
              </label>
              <input
                type="text"
                value={enumValuesInput}
                onChange={(e) => setEnumValuesInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., green, brown"
              />
            </div>
          )}

          {/* Field Destination */}
          <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Field Destination
            </p>

            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Map to Ticket Field
              </label>
              <ThemedSelect
                value={formData.mapsToTicketField}
                onChange={(e) => setFormData({
                  ...formData,
                  mapsToTicketField: e.target.value,
                  mapsToLineItem: e.target.value ? false : formData.mapsToLineItem,
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {TICKET_FIELD_MAPPINGS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </ThemedSelect>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.mapsToLineItem}
                onChange={(e) => setFormData({
                  ...formData,
                  mapsToLineItem: e.target.checked,
                  mapsToTicketField: e.target.checked ? '' : formData.mapsToTicketField,
                })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                disabled={!!formData.mapsToTicketField}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Line Item field (extracted per MPAN/MPRN)
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isRequired}
                onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Required field</span>
            </label>
          </div>

          {/* Form Field Configuration - Only show when mapsToLineItem is true */}
          {formData.mapsToLineItem && (
            <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Line Item Form Field Configuration
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Configure how this field appears in the task/line item form
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Form Field Type
                  </label>
                  <ThemedSelect
                    value={formData.formFieldConfig?.type || 'input'}
                    onChange={(e) => setFormData({
                      ...formData,
                      formFieldConfig: {
                        ...formData.formFieldConfig,
                        type: e.target.value as FormFieldConfig['type'],
                      },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {FORM_FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </ThemedSelect>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Input Subtype
                  </label>
                  <ThemedSelect
                    value={formData.formFieldConfig?.subtype || 'text'}
                    onChange={(e) => setFormData({
                      ...formData,
                      formFieldConfig: {
                        ...formData.formFieldConfig,
                        subtype: e.target.value as FormFieldConfig['subtype'],
                      },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {FORM_FIELD_SUBTYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </ThemedSelect>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Hint Text (placeholder)
                  </label>
                  <input
                    type="text"
                    value={formData.formFieldConfig?.hint || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      formFieldConfig: {
                        ...formData.formFieldConfig,
                        hint: e.target.value,
                      },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Enter MPAN Number"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Max Length (0 = unlimited)
                  </label>
                  <input
                    type="number"
                    value={formData.formFieldConfig?.maxLength || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      formFieldConfig: {
                        ...formData.formFieldConfig,
                        maxLength: parseInt(e.target.value) || 0,
                      },
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="0"
                  />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.formFieldConfig?.isDisabled || false}
                    onChange={(e) => setFormData({
                      ...formData,
                      formFieldConfig: {
                        ...formData.formFieldConfig,
                        isDisabled: e.target.checked,
                      },
                    })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Read-only field</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.formFieldConfig?.isDeletable || false}
                    onChange={(e) => setFormData({
                      ...formData,
                      formFieldConfig: {
                        ...formData.formFieldConfig,
                        isDeletable: e.target.checked,
                      },
                    })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">User can delete</span>
                </label>
              </div>
            </div>
          )}
        </form>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!formData.key || !formData.displayName || isSaving}>
            {isSaving ? <Spinner size="sm" className="mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {field ? 'Update Field' : 'Add Field'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Request Mapping Row Component
function RequestMappingRow({
  mapping,
  lineItemFields,
  onUpdate,
  onRemove,
}: {
  mapping: RequestFieldMapping;
  lineItemFields: SchemaField[];
  onUpdate: (updates: Partial<RequestFieldMapping>) => void;
  onRemove: () => void;
}) {
  const [showTransform, setShowTransform] = useState(
    mapping.transformation && mapping.transformation.type !== TransformationType.NONE
  );

  return (
    <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex items-start gap-3">
        <GripVertical className="h-4 w-4 text-gray-400 mt-2.5 flex-shrink-0 cursor-move dark:text-gray-500" />

        <div className="flex-1 grid grid-cols-12 gap-3">
          {/* Source Field */}
          <div className="col-span-4">
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Source Field (Line Item)</label>
            <ThemedSelect
              value={mapping.sourceField}
              onChange={(e) => onUpdate({ sourceField: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Select field...</option>
              {lineItemFields.map((f) => (
                <option key={f.key} value={f.key}>{f.displayName} ({f.key})</option>
              ))}
              <optgroup label="Common Fields">
                {COMMON_SOURCE_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </optgroup>
            </ThemedSelect>
          </div>

          {/* Arrow */}
          <div className="col-span-1 flex items-center justify-center pt-5">
            <ArrowRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </div>

          {/* Target Field */}
          <div className="col-span-4">
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Target Field (API Request)</label>
            <input
              type="text"
              value={mapping.targetField}
              onChange={(e) => onUpdate({ targetField: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="e.g., MPAN, eac"
            />
          </div>

          {/* Options */}
          <div className="col-span-3 flex items-end gap-2 pb-0.5">
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={mapping.isRequired}
                onChange={(e) => onUpdate({ isRequired: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700"
              />
              Required
            </label>
            <button
              type="button"
              onClick={() => setShowTransform(!showTransform)}
              className={`text-xs px-2 py-1 rounded ${showTransform ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'bg-gray-100 text-gray-600 hover:bg-primary-100 dark:hover:bg-primary-900/30'}`}
            >
              Transform
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="text-red-500 hover:text-red-700 p-1"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Transformation Options */}
      {showTransform && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 ml-7">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Transformation</label>
              <ThemedSelect
                value={mapping.transformation?.type || TransformationType.NONE}
                onChange={(e) => onUpdate({
                  transformation: {
                    type: e.target.value as TransformationType,
                    replacePattern: mapping.transformation?.replacePattern,
                    replaceWith: mapping.transformation?.replaceWith,
                  }
                })}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
              >
                {TRANSFORMATION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </ThemedSelect>
            </div>

            {mapping.transformation?.type === TransformationType.REPLACE && (
              <>
                <div className="col-span-4">
                  <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Pattern (regex)</label>
                  <input
                    type="text"
                    value={mapping.transformation?.replacePattern || ''}
                    onChange={(e) => onUpdate({
                      transformation: {
                        ...mapping.transformation!,
                        replacePattern: e.target.value,
                      }
                    })}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                    placeholder="e.g., \s+"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Replace with</label>
                  <input
                    type="text"
                    value={mapping.transformation?.replaceWith || ''}
                    onChange={(e) => onUpdate({
                      transformation: {
                        ...mapping.transformation!,
                        replaceWith: e.target.value,
                      }
                    })}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                    placeholder="e.g., -"
                  />
                </div>
              </>
            )}

            <div className="col-span-4">
              <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Default Value</label>
              <input
                type="text"
                value={mapping.defaultValue || ''}
                onChange={(e) => onUpdate({ defaultValue: e.target.value })}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                placeholder="Value if field is empty"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Response Mapping Row Component
function ResponseMappingRow({
  mapping,
  onUpdate,
  onRemove,
}: {
  mapping: ResponseFieldMapping;
  onUpdate: (updates: Partial<ResponseFieldMapping>) => void;
  onRemove: () => void;
}) {
  const [showTransform, setShowTransform] = useState(
    mapping.transformation && mapping.transformation.type !== TransformationType.NONE
  );

  return (
    <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="flex items-start gap-3">
        <GripVertical className="h-4 w-4 text-gray-400 mt-2.5 flex-shrink-0 cursor-move dark:text-gray-500" />

        <div className="flex-1 grid grid-cols-12 gap-3">
          {/* Source Field (API Response) */}
          <div className="col-span-5">
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Source Field (API Response)</label>
            <input
              type="text"
              value={mapping.sourceField}
              onChange={(e) => onUpdate({ sourceField: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="e.g., data.quotationLink, brokerLink"
            />
            <p className="text-xs text-gray-400 mt-0.5 dark:text-gray-500">Supports dot notation: data.nested.field</p>
          </div>

          {/* Arrow */}
          <div className="col-span-1 flex items-center justify-center pt-5">
            <ArrowRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </div>

          {/* Target Field (Line Item) */}
          <div className="col-span-4">
            <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Target Field (Line Item)</label>
            <input
              type="text"
              value={mapping.targetField}
              onChange={(e) => onUpdate({ targetField: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="e.g., quotationLink"
            />
          </div>

          {/* Options */}
          <div className="col-span-2 flex items-end gap-2 pb-0.5">
            <button
              type="button"
              onClick={() => setShowTransform(!showTransform)}
              className={`text-xs px-2 py-1 rounded ${showTransform ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'bg-gray-100 text-gray-600 hover:bg-primary-100 dark:hover:bg-primary-900/30'}`}
            >
              Transform
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="text-red-500 hover:text-red-700 p-1"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Transformation Options */}
      {showTransform && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 ml-7">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-4">
              <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Transformation</label>
              <ThemedSelect
                value={mapping.transformation?.type || TransformationType.NONE}
                onChange={(e) => onUpdate({
                  transformation: {
                    type: e.target.value as TransformationType,
                    replacePattern: mapping.transformation?.replacePattern,
                    replaceWith: mapping.transformation?.replaceWith,
                  }
                })}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
              >
                {TRANSFORMATION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </ThemedSelect>
            </div>

            {mapping.transformation?.type === TransformationType.REPLACE && (
              <>
                <div className="col-span-4">
                  <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Pattern (regex)</label>
                  <input
                    type="text"
                    value={mapping.transformation?.replacePattern || ''}
                    onChange={(e) => onUpdate({
                      transformation: {
                        ...mapping.transformation!,
                        replacePattern: e.target.value,
                      }
                    })}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                    placeholder="e.g., http://"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-xs text-gray-500 mb-1 dark:text-gray-400">Replace with</label>
                  <input
                    type="text"
                    value={mapping.transformation?.replaceWith || ''}
                    onChange={(e) => onUpdate({
                      transformation: {
                        ...mapping.transformation!,
                        replaceWith: e.target.value,
                      }
                    })}
                    className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                    placeholder="e.g., https://"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ExtractionSchemasSettings;
